import React from 'react';
import classNames from 'classnames';

// Utils
import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { LISTING_STATE_PUBLISHED } from '../../../util/types';
import {
  ABOUT_LESSON_KEY,
  LANGUAGES_KEY,
  LESSON_FOCUS_KEY,
  PICKUP_KEY,
  SUBURBS_KEY,
  TRANSMISSION_KEY,
  formatVehicleName,
  getOptionLabels,
  getShortAddress,
  getVehicle,
  VEHICLE_FIELD_KEYS,
} from '../../../util/instructorListing';

// Shared components
import { AvatarLarge, MarketingIcon } from '../../../components';

// Same directory
import css from './InstructorProfile.module.css';

const hasText = value => typeof value === 'string' && value.trim().length > 0;

/**
 * Average star rating of the given reviews, to one decimal place.
 *
 * @param {Array<Object>} reviews review entities
 * @returns {number|null}
 */
export const averageRating = reviews => {
  const ratings = (reviews || [])
    .map(review => review?.attributes?.rating)
    .filter(rating => typeof rating === 'number');
  if (ratings.length === 0) {
    return null;
  }
  const sum = ratings.reduce((total, rating) => total + rating, 0);
  return Math.round((sum / ratings.length) * 10) / 10;
};

const Chips = props => (
  <ul className={css.chips}>
    {props.items.map(({ option, label }) => (
      <li key={option} className={css.chip}>
        {label}
      </li>
    ))}
  </ul>
);

const Section = props => {
  const { id, title, children } = props;
  return (
    <section className={css.section} aria-labelledby={id}>
      <h2 id={id} className={css.sectionTitle}>
        {title}
      </h2>
      {children}
    </section>
  );
};

/**
 * The top of an instructor's listing page: who they are, whether they're verified, where they
 * teach and their rating, followed by what they teach, their car and their area.
 * Reads Drivelo's Console listing fields by key (see util/instructorListing.js).
 *
 * @component
 * @param {Object} props
 * @param {Object} props.listing the listing entity, with its author
 * @param {ReactNode} props.title listing title, possibly with long words broken up
 * @param {string?} props.description listing description
 * @param {Array<Object>} props.listingFields config.listing.listingFields
 * @param {Array<Object>} props.reviews review entities for the listing
 * @param {boolean?} props.hasActionBarAbove the owner's action bar overlays the top on phones
 * @returns {JSX.Element}
 */
const InstructorProfile = props => {
  const { listing, title, description, listingFields, reviews, hasActionBarAbove } = props;
  const intl = useIntl();
  const { publicData = {}, state } = listing?.attributes || {};
  const author = listing?.author;
  const name = author?.attributes?.profile?.displayName;
  const nameValues = { name: name || intl.formatMessage({ id: 'InstructorProfile.fallbackName' }) };

  const address = getShortAddress(publicData);
  const isVerified = state === LISTING_STATE_PUBLISHED;
  const rating = averageRating(reviews);
  const reviewCount = reviews?.length || 0;

  const transmissions = getOptionLabels(
    listingFields,
    TRANSMISSION_KEY,
    publicData[TRANSMISSION_KEY]
  );
  const lessonFocus = getOptionLabels(
    listingFields,
    LESSON_FOCUS_KEY,
    publicData[LESSON_FOCUS_KEY]
  );
  const pickup = getOptionLabels(listingFields, PICKUP_KEY, publicData[PICKUP_KEY])[0];
  const ancap = getOptionLabels(
    listingFields,
    VEHICLE_FIELD_KEYS.ancap,
    publicData[VEHICLE_FIELD_KEYS.ancap]
  )[0];
  const languages = getOptionLabels(listingFields, LANGUAGES_KEY, publicData[LANGUAGES_KEY]);
  const suburbs = publicData[SUBURBS_KEY];
  const aboutLesson = publicData[ABOUT_LESSON_KEY];

  const vehicle = getVehicle(publicData);
  const vehicleName = formatVehicleName(vehicle);
  // Without car details, the transmission is shown with the lesson chips instead
  const showCar = !!vehicleName || !!ancap;
  const teachChips = showCar ? lessonFocus : [...transmissions, ...lessonFocus];
  const carRows = [
    vehicleName ? { key: 'car', label: 'InstructorProfile.carLabel', value: vehicleName } : null,
    transmissions.length > 0
      ? {
          key: 'transmission',
          label: 'InstructorProfile.transmissionLabel',
          value: transmissions.map(t => t.label).join(', '),
        }
      : null,
    ancap ? { key: 'ancap', label: 'InstructorProfile.ancapLabel', value: ancap.label } : null,
  ].filter(Boolean);

  return (
    <div className={classNames(css.root, { [css.rootBelowActionBar]: hasActionBarAbove })}>
      <header className={css.header}>
        {author ? <AvatarLarge user={author} className={css.avatar} /> : null}
        <div className={css.headerText}>
          <p className={css.eyebrow}>
            {address ? (
              <FormattedMessage
                id="InstructorProfile.eyebrowWithPlace"
                values={{ place: address }}
              />
            ) : (
              <FormattedMessage id="InstructorProfile.eyebrow" />
            )}
          </p>
          <h1 className={css.title}>{title}</h1>
          <ul className={css.meta}>
            {name ? <li className={css.metaName}>{name}</li> : null}
            {isVerified ? (
              <li className={css.verified}>
                <MarketingIcon name="shield" className={css.metaIcon} />
                <FormattedMessage id="InstructorProfile.verified" />
              </li>
            ) : null}
            {rating ? (
              <li className={css.metaItem}>
                <MarketingIcon name="star" className={css.metaIcon} />
                <FormattedMessage
                  id="InstructorProfile.rating"
                  values={{
                    rating: intl.formatNumber(rating, { minimumFractionDigits: 1 }),
                    count: reviewCount,
                  }}
                />
              </li>
            ) : null}
            {address ? (
              <li className={css.metaItem}>
                <MarketingIcon name="pin" className={css.metaIcon} />
                {address}
              </li>
            ) : null}
            {languages.length > 0 ? (
              <li className={css.metaItem}>
                <FormattedMessage
                  id="InstructorProfile.languages"
                  values={{ languages: languages.map(l => l.label).join(', ') }}
                />
              </li>
            ) : null}
          </ul>
        </div>
      </header>

      {hasText(description) || hasText(aboutLesson) ? (
        <Section
          id="instructor-about"
          title={<FormattedMessage id="InstructorProfile.aboutTitle" values={nameValues} />}
        >
          {hasText(description) ? <p className={css.text}>{description}</p> : null}
          {hasText(aboutLesson) ? <p className={css.text}>{aboutLesson}</p> : null}
        </Section>
      ) : null}

      {teachChips.length > 0 ? (
        <Section
          id="instructor-teaches"
          title={<FormattedMessage id="InstructorProfile.teachesTitle" values={nameValues} />}
        >
          <Chips items={teachChips} />
        </Section>
      ) : null}

      {showCar ? (
        <Section id="instructor-car" title={<FormattedMessage id="InstructorProfile.carTitle" />}>
          <dl className={css.facts}>
            {carRows.map(({ key, label, value }) => (
              <div key={key} className={css.fact}>
                <dt className={css.factLabel}>
                  <FormattedMessage id={label} />
                </dt>
                <dd className={css.factValue}>{value}</dd>
              </div>
            ))}
          </dl>
          <p className={css.note}>
            <FormattedMessage id="InstructorProfile.carNote" />
          </p>
        </Section>
      ) : null}

      {hasText(suburbs) || pickup ? (
        <Section
          id="instructor-area"
          title={<FormattedMessage id="InstructorProfile.areaTitle" values={nameValues} />}
        >
          {hasText(suburbs) ? <p className={css.text}>{suburbs}</p> : null}
          {pickup ? (
            <p className={classNames(css.text, css.pickup)}>
              <MarketingIcon name="pin" className={css.metaIcon} />
              {pickup.label}
            </p>
          ) : null}
        </Section>
      ) : null}
    </div>
  );
};

export default InstructorProfile;
